import useAuth from '@/hooks/useAuth';
import { useSearchParams } from '@/hooks/useSearchParams';
import IBookingRules from '@/types/IBookingRules';
import IThirdPartyCalendar from '@/types/IThirdPartyCalendar';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

interface IArgs {
	bookingRules: IBookingRules;
	onChange: (values: Partial<IBookingRules>) => void;
}

export function useSectionCalendars(args: IArgs) {
	const isFetching = useIsFetching() > 0;
	const isMutating = useIsMutating() > 0;
	const isLoading = isFetching || isMutating;
	const { t } = useTranslation();
	const auth = useAuth();
	const calendars = auth.getCurrentUser().thirdPartyCalendars;
	const groupedCalendars = _groupByAccount(calendars);
	const params = useSearchParams();
	const history = useHistory();

	function handleCalendarClick(id: string, isChecked: boolean) {
		const checkAvailabilityInCalendars = structuredClone(args.bookingRules.checkAvailabilityInCalendars).filter(
			(c) => c.id !== id
		);

		if (isChecked) {
			checkAvailabilityInCalendars.push({ id });
		}

		return args.onChange({ checkAvailabilityInCalendars });
	}

	function showModal() {
		params.set('add-calendar-modal', 'true');
		history.replace({ search: params.toString() });
	}

	function hideModal() {
		params.delete('add-calendar-modal');
		history.replace({ search: params.toString() });
	}

	return {
		isModalVisible: params.get('add-calendar-modal') === 'true',
		t,
		isLoading,
		groupedCalendars,
		handleCalendarClick,
		showModal,
		hideModal,
	};
}

function _groupByAccount(calendars: Array<IThirdPartyCalendar>): {
	[account: string]: Array<IThirdPartyCalendar>;
} {
	const result: { [account: string]: Array<IThirdPartyCalendar> } = {};

	for (const calendar of calendars) {
		if (!result[calendar.account]) {
			result[calendar.account] = [];
		}

		result[calendar.account].push(calendar);
	}

	// Sort by primary calendar first and by name
	for (const account in result) {
		result[account].sort((a, b) => {
			if (a.primary && b.primary) {
				return a.name.localeCompare(b.name);
			}

			if (a.primary && !b.primary) {
				return -1;
			}

			if (!a.primary && b.primary) {
				return 1;
			}

			return a.name.localeCompare(b.name);
		});
	}

	return result;
}
